import { existsSync } from 'node:fs';
import path from 'node:path';

import { rawProjects } from '@/data/projects';
import {
  type ExperienceEntry,
  type Project,
  type Scene,
  projectSchema
} from '@/lib/schemas';

function ensurePublicAssetExists(publicPath: string) {
  if (!publicPath.startsWith('/')) {
    throw new Error(`Asset path must start with "/": ${publicPath}`);
  }

  const assetPath = path.join(process.cwd(), 'public', publicPath.slice(1));
  if (!existsSync(assetPath)) {
    throw new Error(`Missing asset file: ${publicPath}`);
  }
}

function validateProject(project: Project) {
  const sceneById = new Map<string, Scene>();
  const sceneSlugSet = new Set<string>();
  const entrySlugSet = new Set<string>();

  for (const scene of project.scenes) {
    if (sceneById.has(scene.id)) {
      throw new Error(`Duplicate scene id "${scene.id}" in project "${project.slug}"`);
    }
    if (sceneSlugSet.has(scene.slug)) {
      throw new Error(`Duplicate scene slug "${scene.slug}" in project "${project.slug}"`);
    }
    sceneById.set(scene.id, scene);
    sceneSlugSet.add(scene.slug);

    ensurePublicAssetExists(scene.targetPreview);

    if (scene.arContent.kind === 'video' || scene.arContent.kind === 'image') {
      ensurePublicAssetExists(scene.arContent.src);
    }
    if (scene.arContent.poster) {
      ensurePublicAssetExists(scene.arContent.poster);
    }
    if (scene.fallback.altContentType !== 'link') {
      ensurePublicAssetExists(scene.fallback.altContentSrc);
    }
  }

  for (const entry of project.entries) {
    if (entry.projectId !== project.id) {
      throw new Error(`Entry "${entry.id}" references unexpected project id "${entry.projectId}"`);
    }
    if (entrySlugSet.has(entry.slug)) {
      throw new Error(`Duplicate entry slug "${entry.slug}" in project "${project.slug}"`);
    }
    entrySlugSet.add(entry.slug);
    ensurePublicAssetExists(entry.mindFile);

    if (entry.mode === 'single' && entry.sceneIds.length !== 1) {
      throw new Error(`Single entry "${entry.slug}" must reference exactly one scene`);
    }
    if (entry.mode === 'multi' && entry.sceneIds.length < 2) {
      throw new Error(`Multi entry "${entry.slug}" must reference at least two scenes`);
    }

    const seenTargetIndexes = new Set<number>();
    for (const sceneId of entry.sceneIds) {
      const scene = sceneById.get(sceneId);
      if (!scene) {
        throw new Error(`Entry "${entry.slug}" references unknown scene id "${sceneId}"`);
      }
      if (seenTargetIndexes.has(scene.targetIndex)) {
        throw new Error(
          `Entry "${entry.slug}" contains duplicate targetIndex "${scene.targetIndex}" across scenes`
        );
      }
      seenTargetIndexes.add(scene.targetIndex);
    }
  }
}

const validatedProjects = rawProjects.map((project) => {
  const parsed = projectSchema.parse(project);
  validateProject(parsed);
  return parsed;
});

export const projects = validatedProjects;

export type ExperiencePayload = {
  project: Project;
  entry: ExperienceEntry;
  scenes: Scene[];
};

export function getProjects() {
  return projects;
}

export function getProjectBySlug(projectSlug: string) {
  return projects.find((project) => project.slug === projectSlug) ?? null;
}

export function getSceneBySlug(projectSlug: string, sceneSlug: string) {
  const project = getProjectBySlug(projectSlug);
  if (!project) return null;
  const scene = project.scenes.find((item) => item.slug === sceneSlug) ?? null;
  return scene ? { project, scene } : null;
}

export function getEntryBySlug(projectSlug: string, entrySlug: string): ExperiencePayload | null {
  const project = getProjectBySlug(projectSlug);
  if (!project) return null;
  const entry = project.entries.find((item) => item.slug === entrySlug) ?? null;
  if (!entry) return null;

  const scenes = entry.sceneIds
    .map((sceneId) => project.scenes.find((scene) => scene.id === sceneId))
    .filter((scene): scene is Scene => Boolean(scene));

  return { project, entry, scenes };
}

export function getPrimaryEntryForScene(project: Project, sceneId: string) {
  return (
    project.entries.find((entry) => entry.mode === 'single' && entry.sceneIds[0] === sceneId) ??
    project.entries.find((entry) => entry.sceneIds.includes(sceneId)) ??
    null
  );
}

export function buildProjectPath(projectSlug: string) {
  return `/${projectSlug}`;
}

export function buildScenePath(projectSlug: string, sceneSlug: string) {
  return `/${projectSlug}/scenes/${sceneSlug}`;
}

export function buildExperiencePath(projectSlug: string, entrySlug: string) {
  return `/${projectSlug}/experience/${entrySlug}`;
}
